import * as React from 'react';
import { cancelIcon, editIcon, saveIcon } from './icons';
import { PopupMenu } from './PopupMenu';
import { useEffect } from 'react';

export type SaveChangesResult = { success: true } | { success: false; errors: string[] };
export type SaveChangePositionResult = { success: true } | { success: false; errors: string[] };

interface InlineFormItemProps {
    errors?: string[];
    className?: string;
    link: JSX.Element;
    position: number;
    children: React.ReactNode;
    saveChanges(): Promise<SaveChangesResult>;
    savePositionChanges?(oldPosition: number, placeBefore: boolean): Promise<SaveChangesResult>;
    formRef?: React.RefObject<HTMLFormElement>;
    onStartEditing?(): void;
    onCancelEditing?(): void;
    onFinishEditing?(): void;
    editState?: boolean;
    options: React.ReactNode;
    optionsTitle?: string;
    draggable: boolean;
    enableEditButton: boolean;
    enablePopup: boolean;
}

type inlineFormState = "up" | "down" | "";

export const InlineFormItem = (props: InlineFormItemProps) => {
    const [isEditing, setIsEditing] = React.useState<boolean>();
    const [errors, setErrors] = React.useState<string[]>([]);
    const [dragStartedHere, setDragStartedHere] = React.useState<boolean>(false);
    const [inlineFormClassName, setInlineFormClassName] = React.useState<inlineFormState>("");

    useEffect(() => {
        if (typeof props.editState !== 'undefined') {
          setIsEditing(props.editState);
        }
    }, []);

    useEffect(() => {
        props.errors && setErrors(props.errors);
    }, []);

    const toggleEditing = () => {
        if (isEditing) {
            if (formRef.current && formRef.current.checkValidity()) {
                setIsEditing(false);
                props.onCancelEditing && props.onCancelEditing();
            }
        } else {
            setIsEditing(true);
            props.onStartEditing && props.onStartEditing();
        }
    };

    const cancelOnEscape = (e: React.KeyboardEvent<HTMLFormElement>) => {
        if (e.key === 'Escape') {
            setIsEditing(false);
            props.onCancelEditing && props.onCancelEditing();
        }
    };

    const resetErrors = () => setErrors([]);

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        if (formRef.current && formRef.current.checkValidity()) {
            props.saveChanges().then(result => {
                if (result.success) {
                    setIsEditing(false);
                    props.onFinishEditing && props.onFinishEditing();
                } else {
                    setErrors(result.errors);
                }
            });
        }
        e.preventDefault();
    };

    const formRef = props.formRef || React.createRef();

    const handleDragStart = (e: React.DragEvent, pos: string) => {
        e.dataTransfer.setData("pos", pos);

        setDragStartedHere(true)
    }

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();

        if (dragStartedHere) {
            return;
        }
        const placeBefore = inlineFormClassName === "up";

        if (props.savePositionChanges) {
            props.savePositionChanges(+e.dataTransfer.getData("pos"), placeBefore).then(result => {
                if (result.success) {
                    setIsEditing(false);
                } else {
                    setErrors(result.errors);
                }
            });
        }
    }

    const handleDragOver = (e: React.DragEvent, pos: number) => {
        e.stopPropagation();
        e.preventDefault();

        if (dragStartedHere) {
            return;
        }

        const dragEffectOffset = 20; //moet gelijk zijn aan bordersize in scss

        const hoverBoundingRect = e.currentTarget.getBoundingClientRect();
        var hoverMiddleY = 0;
        var switchPoint = 0;

        if (inlineFormClassName === "") {
            hoverMiddleY = (hoverBoundingRect.height) / 2;
            switchPoint = hoverBoundingRect.top + hoverMiddleY;
        }
        if (inlineFormClassName === "down") {
            hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top - dragEffectOffset) / 2;
            switchPoint = hoverBoundingRect.top + hoverMiddleY
        }
        if (inlineFormClassName === "up") {
            hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top - dragEffectOffset) / 2;
            switchPoint = hoverBoundingRect.bottom - hoverMiddleY
        }

        if (e.clientY < switchPoint) {
            setInlineFormClassName("up");
        } else {
            setInlineFormClassName("down");
        }
    }
    const handleDragLeave = (e: React.DragEvent) => {
        e.stopPropagation();
        e.preventDefault();

        setInlineFormClassName("")
    }

    const compoundClassName = 'inlineFormItem ' + inlineFormClassName;
    return <div>
        <div data-position={props.position} className={compoundClassName} draggable={props.draggable} onDragLeave={e => handleDragLeave(e)} onDragStart={e => handleDragStart(e, props.position.toString())} onDragOver={e => handleDragOver(e, props.position)} onDrop={e => handleDrop(e)}>
            {isEditing
                ? <span className={`${props.className} linkbutton button--important`}>
                    <form onSubmit={handleSubmit} ref={formRef} onKeyDown={cancelOnEscape} onChange={resetErrors}>
                        {props.children}
                        <button type="submit" className="item__inline-button">{saveIcon}</button>
                        {errors != null && errors.length > 0 && <span style={{ marginLeft: '1em', color: 'red', background: 'white', padding: '0.3em' }}>{errors!.join('. ')}</span>}
                    </form>
                </span>
                : props.link
            }
            {props.enableEditButton &&
                <button className="inlineEditButton" onClick={toggleEditing}>
                    {isEditing ? cancelIcon : editIcon}
                </button>
            }
            {props.enablePopup &&
                <PopupMenu title={props.optionsTitle}>
                    {props.options}
                </PopupMenu>
            }
        </div>
    </div>
}
